// PDF do relatório dos caminhões (E8): para cada caminhão, o custo por km, os
// km, o consumo, os custos, o resultado e a tabela mês a mês; no fim, a frota
// toda. Feito no aparelho (funciona sem sinal). As fontes padrão do PDF não
// têm o sinal de menos tipográfico: vai o hífen.
import { data as formatarData, documento as formatarDocumento, mesCurto, numeroBR, placa as formatarPlaca } from './formato.js';
import { rodape } from './demonstrativo.js';
import { DESPESAS, RATEIOS, ultimoDiaDoMes } from './custos.js';

// Por que o custo por km de um mês ficou sem conta (o mesmo texto da tela).
export function notaDoMes(m) {
  return m.km.acumulado
    ? `* ${mesCurto(m.mes)}: os km contam desde a leitura de ${formatarData(m.km.desde)}, com meses sem leitura no meio; por isso o custo por km desse mês fica sem conta.`
    : `* ${mesCurto(m.mes)}: a primeira leitura de km é de ${formatarData(m.km.desde)}; os km não cobrem o mês inteiro, e o custo por km dele fica sem conta.`;
}

const valor = (v) => numeroBR(v, { casas: 2 });
const reais = (v) => (v < 0 ? `-R$ ${valor(-v)}` : `R$ ${valor(v)}`);

// O período é de meses inteiros (do dia 1 ao último dia)?
const mesesInteiros = ({ de, ate }) => de.endsWith('-01') && ate === ultimoDiaDoMes(ate.slice(0, 7));

// "caminhoes-2026-09-a-2026-10.pdf" · entre datas, "caminhoes-2026-10-05-a-2026-10-20.pdf"
// · de um caminhão só, com a placa: "caminhoes-MDT7J86-2026-10.pdf"
export function nomeDoArquivoDosCaminhoes(r) {
  const { de, ate } = r;
  const placa = r.veiculoId && r.caminhoes.length === 1 ? `-${String(r.caminhoes[0].veiculo.placa ?? '').replace(/[^A-Z0-9]/gi, '')}` : '';
  if (!mesesInteiros(r)) return de === ate ? `caminhoes${placa}-${de}.pdf` : `caminhoes${placa}-${de}-a-${ate}.pdf`;
  const mes = (d) => d.slice(0, 7);
  return mes(de) === mes(ate) ? `caminhoes${placa}-${mes(de)}.pdf` : `caminhoes${placa}-${mes(de)}-a-${mes(ate)}.pdf`;
}

export function periodoDoRelatorio(r) {
  const { de, ate } = r;
  if (!mesesInteiros(r)) return de === ate ? formatarData(de) : `${formatarData(de)} a ${formatarData(ate)}`;
  return de.slice(0, 7) === ate.slice(0, 7) ? mesCurto(de) : `${mesCurto(de)} a ${mesCurto(ate)}`;
}

// Um pedaço do mês na tabela (período entre datas): "5 a 20/10"
export function rotuloDoTrecho(m) {
  return `${Number(m.de.slice(8))} a ${Number(m.ate.slice(8))}/${m.mes.slice(5)}`;
}

// "9 parcelas em aberto em 31/10/2026"
const textoDoEmAberto = ({ parcelas }, ate) => `${parcelas === 1 ? '1 parcela' : `${parcelas} parcelas`} em aberto em ${formatarData(ate)}`;

// As linhas de custo de um caminhão (sem as zeradas), como na tela, com a
// linha dos lançamentos de cada uma: [rótulo, valor, linha].
export function linhasDeCusto(c) {
  const g = c.porGrupo;
  const cent = (v) => Math.round((v ?? 0) * 100);
  return [
    ['Diesel', c.diesel, 'diesel'],
    ['Outros gastos da viagem (pedágio, alimentação, chapa...)', (cent(g.viagem) - cent(c.diesel)) / 100, 'viagem'],
    ['Manutenção e pneus', g.manutencao ?? 0, 'manutencao'],
    ['Fixos (seguro, IPVA, financiamento...)', g.fixo ?? 0, 'fixo'],
    ['Outras despesas', (cent(c.despesas) - cent(g.viagem) - cent(g.manutencao) - cent(g.fixo)) / 100, 'outros'],
    ['Parte das despesas gerais', c.gerais, 'gerais'],
    [c.remuneracao.prevista !== 0 ? `Motorista (${reais(c.remuneracao.prevista)} pelas regras, ainda não acertado)` : 'Motorista', c.remuneracao.total, 'motorista'],
    ['Agenciamentos (à agenciadora ou à transportadora)', c.agenciamentos, 'agenciamento'],
  ].filter(([, v]) => Math.round(v * 100) !== 0);
}

const itensDa = (c, chave) => (c.itens ?? []).filter((i) => i.linha === chave);

export async function caminhoesEmPdf({ relatorio: r, empresa, comResultado = true, emitidoEm = new Date() }) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const margem = 18;
  const direita = 210 - margem;
  const fim = 280;
  let y = 24;
  const cabe = (altura) => {
    if (y + altura > fim) {
      pdf.addPage();
      y = 20;
    }
  };
  const fuso = empresa.fuso_horario || 'America/Sao_Paulo';
  const emitido = `${new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, day: '2-digit', month: '2-digit', year: 'numeric' }).format(emitidoEm)}`
    + ` às ${new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, hour: '2-digit', minute: '2-digit' }).format(emitidoEm)}`;
  const doc = empresa.documento ? ` · ${empresa.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'} ${formatarDocumento(empresa.documento)}` : '';
  const texto = (t, x, opcoes = {}) => pdf.text(String(t).replace(/−/g, '-'), x, y, opcoes);
  const pequeno = (t, largura = direita - margem) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(90);
    const linhas = pdf.splitTextToSize(String(t).replace(/−/g, '-'), largura);
    cabe(linhas.length * 3.6 + 1);
    pdf.text(linhas, margem, y);
    pdf.setTextColor(0);
    y += linhas.length * 3.6 + 1.4;
  };
  const linhaValor = (rotulo, v, { negrito = false } = {}) => {
    cabe(6);
    pdf.setFont('helvetica', negrito ? 'bold' : 'normal');
    pdf.setFontSize(9.5);
    const partes = pdf.splitTextToSize(String(rotulo).replace(/−/g, '-'), 120);
    pdf.text(partes, margem + 2, y);
    texto(reais(v), direita, { align: 'right' });
    y += partes.length * 4.4 + 0.8;
  };
  // Os lançamentos de uma linha, um embaixo do outro (letra menor, recuados):
  // cada valor discriminado, para conferir (pedido do titular em 13/10/2026).
  const itens = (lista) => {
    for (const i of lista) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      const textoDoItem = `${formatarData(i.data)}  ${i.nome}${i.detalhe ? ` · ${i.detalhe}` : ''}`.replace(/−/g, '-').replace(/→/g, '->').replace(/\u00a0/g, ' ');
      const partes = pdf.splitTextToSize(textoDoItem, 118);
      cabe(partes.length * 3.5 + 1);
      pdf.setTextColor(70);
      pdf.text(partes, margem + 7, y);
      texto(reais(i.valor), direita - 22, { align: 'right' });
      pdf.setTextColor(0);
      y += partes.length * 3.5 + 0.6;
    }
    if (lista.length) y += 0.8;
  };
  const linhaComItens = (rotulo, v, lista, opcoes) => {
    linhaValor(rotulo, v, opcoes);
    itens(lista);
  };

  // Cabeçalho
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  texto(r.veiculoId ? 'RELATÓRIO DO CAMINHÃO' : 'RELATÓRIO DOS CAMINHÕES', margem);
  y += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  texto(`${empresa.nome}${doc}`, margem);
  y += 5;
  texto(mesesInteiros(r) ? `Período: ${periodoDoRelatorio(r)} (${formatarData(r.de)} a ${formatarData(r.ate)})` : `Período: ${periodoDoRelatorio(r)}`, margem);
  y += 5;
  texto(`Emitido em ${emitido}.`, margem);
  y += 5.5;
  pequeno('Custo por km = (despesas do caminhão e da carreta + parte das despesas gerais + motorista + agenciamentos) ÷ km rodados. '
    + 'Km rodados: a maior leitura do mês menos a maior até o mês anterior (abastecimentos, despesas e viagens). '
    + (r.despesas === 'pagamento'
      ? `Despesas ${DESPESAS.pagamento}; fretes e créditos pela data da carga e do crédito; despesas gerais ${RATEIOS[r.rateio]}. `
      : `Custos pela data da despesa, da carga e do crédito; despesas gerais ${RATEIOS[r.rateio]}. `)
    + 'Motorista: o que entrou nos acertos e no histórico, mais o previsto pelas regras para o que ainda não foi acertado.');
  pdf.setLineWidth(0.3);
  pdf.line(margem, y, direita, y);
  y += 7;

  if (r.caminhoes.length === 0) {
    pdf.setFontSize(10);
    texto('Nenhum caminhão com km, despesa ou carga no período.', margem);
    y += 8;
  }

  for (const c of r.caminhoes) {
    cabe(60);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    texto(`${formatarPlaca(c.veiculo.placa ?? '')}${c.veiculo.apelido ? ` · ${c.veiculo.apelido}` : ''}`, margem);
    y += 6;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    texto(`Custo por km: ${c.custoPorKm !== null ? `R$ ${valor(c.custoPorKm)}` : 'sem conta'}`
      + ` · km rodados: ${c.km.km !== null ? numeroBR(c.km.km) : 'sem leitura'}`
      + `${c.consumo ? ` · consumo: ${numeroBR(c.consumo.kmPorLitro, { casas: 2 })} km/l` : ''}`
      + `${c.litros ? ` · ${numeroBR(c.litros)} litros` : ''}`, margem);
    y += 6;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9.5);
    texto('Custos', margem);
    y += 4.6;
    for (const [rotulo, v, chave] of linhasDeCusto(c)) linhaComItens(rotulo, v, itensDa(c, chave));
    linhaValor('= Custos', c.custo, { negrito: true });
    y += 1.5;

    if (comResultado) {
      cabe(26);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9.5);
      texto('Resultado', margem);
      y += 4.6;
      linhaComItens(`Fretes (${c.cargas === 1 ? '1 carga' : `${c.cargas} cargas`})`, c.fretes, itensDa(c, 'frete'));
      if (c.estadias) linhaComItens('+ Estadias', c.estadias, itensDa(c, 'estadia'));
      if (c.descontos) linhaComItens('- Descontos do contratante (INSS, SEST/SENAT, IRRF...)', c.descontos, itensDa(c, 'desconto'));
      linhaValor('- Custos', c.custo);
      linhaValor('= Sobrou', c.sobrou, { negrito: true });
      y += 1.5;
    }
    if (c.emAberto && c.emAberto.valor !== 0) {
      linhaComItens(`Ainda a pagar (${textoDoEmAberto(c.emAberto, r.ate)}; entra nos custos quando for pago)`, c.emAberto.valor, c.emAberto.itens ?? []);
      y += 1.5;
    }

    // Mês a mês
    const meses = c.meses.filter((m) => m.custo !== 0 || m.cargas > 0 || m.km.km !== null);
    if (meses.length > 1) {
      const col = { mes: margem, km: 52, kml: 68, custo: 98, rkm: 116, fretes: 146, sobrou: direita };
      const cabecalho = () => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(90);
        texto('Mês', col.mes);
        texto('Km', col.km, { align: 'right' });
        texto('km/l', col.kml, { align: 'right' });
        texto('Custos', col.custo, { align: 'right' });
        texto('R$/km', col.rkm, { align: 'right' });
        if (comResultado) {
          texto('Fretes', col.fretes, { align: 'right' });
          texto('Sobrou', col.sobrou, { align: 'right' });
        }
        pdf.setTextColor(0);
        y += 1.5;
        pdf.setLineWidth(0.2);
        pdf.line(margem, y, direita, y);
        y += 4;
      };
      cabe(14);
      cabecalho();
      for (const m of meses) {
        if (y + 5 > fim) {
          pdf.addPage();
          y = 20;
          cabecalho();
        }
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        texto(m.inteiro === false ? rotuloDoTrecho(m) : mesCurto(m.mes), col.mes);
        texto(m.km.km === null ? '-' : `${numeroBR(m.km.km)}${m.km.acumulado || m.km.desdeAPrimeira ? '*' : ''}`, col.km, { align: 'right' });
        texto(m.consumo ? numeroBR(m.consumo.kmPorLitro, { casas: 2 }) : '-', col.kml, { align: 'right' });
        texto(valor(m.custo), col.custo, { align: 'right' });
        texto(m.custoPorKm !== null ? valor(m.custoPorKm) : '-', col.rkm, { align: 'right' });
        if (comResultado) {
          texto(valor(m.fretes), col.fretes, { align: 'right' });
          texto(valor(m.sobrou), col.sobrou, { align: 'right' });
        }
        y += 4.4;
      }
      y += 1;
    }
    for (const m of meses.filter((x) => x.km.acumulado || x.km.desdeAPrimeira)) pequeno(notaDoMes(m));
    if (c.km.dentroDoPeriodo) pequeno(`Km do período contados da primeira leitura dele (${formatarData(c.km.desde)}): a leitura anterior é de ${formatarData(c.km.ultimaAntes)}.`);
    else if (c.km.desdeAPrimeira && !c.km.semLeitura) pequeno(`Km contados da primeira leitura (${formatarData(c.km.desde)}).`);
    y += 5;
  }

  if (r.caminhoes.length > 1) {
    const soma = (campo) => r.caminhoes.reduce((t, c) => t + Math.round(c[campo] * 100), 0) / 100;
    const fora = r.gerais.semRateio;
    cabe(40);
    pdf.setLineWidth(0.3);
    pdf.line(margem, y - 3, direita, y - 3);
    y += 4;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    texto('Toda a frota', margem);
    y += 6;
    if (comResultado) {
      linhaValor('Fretes e estadias', soma('fretes') + soma('estadias'));
      if (soma('descontos')) linhaValor('- Descontos do contratante', soma('descontos'));
    }
    linhaValor(`${comResultado ? '- ' : ''}Custos dos caminhões`, soma('custo'));
    if (fora) linhaComItens(`${comResultado ? '- ' : ''}Despesas gerais fora dos caminhões`, fora, r.gerais.itens ?? []);
    if (r.impostos) linhaComItens(`${comResultado ? '- ' : ''}Impostos e taxas`, r.impostos, r.itensImpostos ?? []);
    linhaValor(comResultado ? '= Sobrou' : '= Total',
      comResultado ? Math.round((soma('sobrou') - fora - r.impostos) * 100) / 100 : Math.round((soma('custo') + fora + r.impostos) * 100) / 100,
      { negrito: true });
    if (r.emAbertoSemCaminhao) {
      const aberto = r.caminhoes.reduce((t, c) => t + Math.round((c.emAberto?.valor ?? 0) * 100), 0) + Math.round(r.emAbertoSemCaminhao.valor * 100);
      if (aberto) linhaComItens(`Ainda a pagar (parcelas em aberto em ${formatarData(r.ate)}; as sem caminhão abaixo)`, aberto / 100, r.emAbertoSemCaminhao.itens ?? []);
    }
  }

  rodape(pdf, `relatório dos caminhões de ${periodoDoRelatorio(r)}`);
  return pdf.output('blob');
}
